import { Contact } from '@api/Trinium/Endpoints/Contact/types';
import { LegLocation } from '@api/Trinium/Endpoints/Leg/types';

export interface FetchLocationRequest {
	terminalNumber: string;
	/** Location ID */
	id: string;
}

export interface FetchLocationsRequest {
	terminalNumber: string;
	/** Search Criteria for the Location Fetch Request applying startsWith */
	startsWith?: string;
}

export interface PostLocationRequest {
	terminalNumber: string;
	location: Partial<Omit<LocationRaw, 'id'>>;
}

export interface PatchLocationRequest {
	terminalNumber: string;
	location: Pick<LocationRaw, 'id'> & Partial<LocationRaw>;
}

export interface PutLocationRequest {
	id: string;
	terminalNumber: string;
	actionType: PutLocationAction;
}

export type PutLocationAction = AddContactAction | UpdateContactAction | DeleteContactAction;

interface AddContactAction {
	action: 'addContact';
	data: {
		isMain?: boolean;
		name?: string;
		phone?: string;
		fax?: string;
		email?: string;
		notes?: string;
		notificationType?: string;
	};
}

interface UpdateContactAction {
	action: 'updateContact';
	data: {
		id?: number;
		isMain?: boolean;
		name?: string;
		phone?: string;
		fax?: string;
		email?: string;
		notes?: string;
		site?: string;
		type?: string;
		notificationType?: string;
	};
}

interface DeleteContactAction {
	action: 'removeContact';
	data: {
		id: number;
	};
}

export enum LocationType {
	Standard,
	Pier,
	Rail,
	Air,
	CFS,
	'Dehire Depot'
}

/** Location schema for /locations collection */
export interface LocationListItem {
	id: string;
	name: string;
	addr1: string;
	addr2: string;
	cityId?: string;
	city: string;
	state: string;
	zip: string;
	areaName?: string;
	phone?: string;
	fax?: string;
	emailAddr?: string;
	usePuNotes?: boolean;
	puNotes?: string;
	useDelvNotes?: boolean;
	delvNotes?: string;
	notes?: string;
}

export interface LocationRaw {
	id: string;
	name: string;
	isTrmUnique?: boolean;
	type: LocationType;
	addr1: string;
	addr2: string;
	cityId?: string;
	city?: CityRaw;
	zip: string;
	cntryId: string;
	cntry?: CountryRaw;
	areaId: string;
	area?: AreaRaw;
	phone?: string;
	fax?: string;
	emailAddr?: string;
	notes?: string;
	puNotes?: string;
	usePuNotes?: boolean;
	delvNotes?: string;
	useDelvNotes?: boolean;
	puWkFrom?: string | null;
	puWkTo?: string | null;
	puSatFrom?: string | null;
	puSatTo?: string | null;
	puSunFrom?: string | null;
	puSunTo?: string | null;
	delvWkFrom?: string | null;
	delvWkTo?: string | null;
	delvSatFrom?: string | null;
	delvSatTo?: string | null;
	delvSunFrom?: string | null;
	delvSunTo?: string | null;
	contact?: Contact[];
}

interface AreaRaw {
	name: string;
}

interface CityRaw {
	name: string;
	state: string;
}

export interface City {
	id: string;
	name: string;
	state: string;
}

export interface FetchCitiesRequest {
	contains?: string;
	/** 1-based page number; the API's default page size applies. */
	page?: number;
}

interface CountryRaw {
	name: string;
}

export interface Country {
	id: string;
	name: string;
}

/** Fields to exclude when converting the Raw API Location object to the UI Location object */
type ExcludedRawLocationFields = 'cityId' | 'city' | 'areaId' | 'area' | 'cntryId' | 'cntry';

export type Location = Omit<LocationRaw, ExcludedRawLocationFields> & {
	locCity: City;
	areaName?: string;
	country?: Country;
};

export function transformLocationResponse({ cityId, city, cntryId, cntry, area, ...location }: LocationRaw) {
	return {
		locCity: cityId
			? {
					id: cityId,
					name: city?.name,
					state: city?.state
				}
			: undefined,
		country: cntryId
			? {
					id: cntryId,
					name: cntry?.name
				}
			: undefined,
		areaName: area?.name,
		...location
	} as Location;
}

/**
 * Mapping Location objects of the Locations API to Leg embedded object type
 */
export function mapLocListItemToLegLoc(apiLocation: LocationListItem) {
	return {
		...apiLocation
	} as LegLocation;
}

/**
 * Mapping function to convert the Locations API to a form Location schema
 */
export function mapLocationToLegLoc({
	id,
	name,
	addr1,
	addr2,
	locCity,
	zip,
	areaName,
	phone,
	fax,
	emailAddr,
	notes,
	puNotes,
	delvNotes
}: Location) {
	return {
		id,
		name,
		addr1,
		addr2,
		cityId: locCity.id,
		city: locCity.name,
		state: locCity.state,
		zip,
		areaName,
		phone,
		fax,
		emailAddr,
		notes,
		puNotes,
		delvNotes
	} as LegLocation;
}
